import { AlertCircle, Pencil, Plus, Trash2, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmModal } from '@/components/patterns/ConfirmModal';
import { EmptyStateBlock } from '@/components/patterns/EmptyStateBlock';
import { SectionHeading } from '@/components/patterns/SectionHeading';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { deleteChild, listChildren } from '@/features/children/api';
import { clearChildCountCache } from '@/hooks/useChildCount';
import { ApiError, NetworkError } from '@/lib/api-client';
import type { Child } from '@/lib/auth-types';
import { STRINGS } from '@/strings/strings';

type LoadError = 'load' | 'network' | null;

export function ChildrenRoute() {
  const navigate = useNavigate();
  const [children, setChildren] = useState<Child[] | null>(null);
  const [loadError, setLoadError] = useState<LoadError>(null);
  const [deleteTarget, setDeleteTarget] = useState<Child | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const S = STRINGS.account.children;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listChildren();
        if (cancelled) return;
        setChildren(list);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NetworkError) setLoadError('network');
        else setLoadError('load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdd = () => navigate('/account/child_profile?mode=new');
  const handleEdit = (child: Child) => navigate(`/account/child_profile?childId=${child.id}`);

  const handleDeleteRequest = (child: Child) => {
    setDeleteError(null);
    setDeleteTarget(child);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setIsDeleting(true);
    try {
      await deleteChild(target.id);
      setChildren((prev) => prev?.filter((c) => c.id !== target.id) ?? null);
      clearChildCountCache();
      setDeleteTarget(null);
      setDeleteError(null);
    } catch (err) {
      if (err instanceof NetworkError) {
        setDeleteError(S.errors.networkError);
      } else if (err instanceof ApiError) {
        setDeleteError(S.errors.deleteFailed);
      } else {
        setDeleteError(S.errors.deleteFailed);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const loadErrorMessage =
    loadError === 'network'
      ? S.errors.networkError
      : loadError === 'load'
        ? S.errors.loadFailed
        : null;

  const isEmpty = children !== null && children.length === 0;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="default">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {S.back}
          </button>

          <SectionHeading heading={S.title} className="mb-4" />

          {loadErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{loadErrorMessage}</span>
            </div>
          )}

          {deleteError && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{deleteError}</span>
            </div>
          )}

          {/* Children list (when we have data and there's at least one row) */}
          {children !== null && children.length > 0 && (
            <ul className="flex flex-col gap-2" data-testid="children-list">
              {children.map((child) => (
                <li
                  key={child.id}
                  className="flex items-center gap-3 rounded-lg border border-dream-navy/10 bg-dream-white p-3"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-dream-navy/10 text-sm font-semibold text-dream-navy">
                    {child.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate text-sm font-semibold text-dream-navy">{child.name}</p>
                    <p className="text-xs text-dream-navy/60">
                      {child.age} {S.ageSuffix}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleEdit(child)}
                    aria-label={S.editAriaLabel}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-dream-navy hover:bg-dream-navy/5 transition-colors"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteRequest(child)}
                    aria-label={S.deleteAriaLabel}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-error hover:bg-error/10 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Add CTA — shown only when list rendered (empty-state has its own CTA) */}
          {children !== null && children.length > 0 && (
            <Button
              type="button"
              onClick={handleAdd}
              className="mt-4 w-full h-12 text-base font-semibold"
            >
              <Plus className="h-4 w-4" />
              {S.addCta}
            </Button>
          )}

          {/* Empty state */}
          {isEmpty && (
            <EmptyStateBlock
              icon={<Users className="h-10 w-10 text-dream-navy/40" aria-hidden="true" />}
              heading={S.emptyHeading}
              body={S.emptyBody}
              cta={{
                label: S.emptyCta,
                onClick: handleAdd,
                icon: <Plus className="h-4 w-4" aria-hidden="true" />,
              }}
              testId="children-empty-state"
            />
          )}
        </div>
      </ContentContainer>

      {/* A-08 Delete-Child-Confirm (Mode A — destructive variant) */}
      <ConfirmModal
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        headline={S.deleteModal.headline}
        body={deleteTarget ? S.deleteModal.body.replace('{name}', deleteTarget.name) : ''}
        primaryCta={S.deleteModal.primaryCta}
        secondaryCta={S.deleteModal.secondaryCta}
        onConfirm={() => void handleDeleteConfirm()}
        variant="destructive"
        testId="delete-child-confirm-modal"
      />

      {isDeleting && (
        <div className="sr-only" aria-live="polite">
          Wird gelöscht…
        </div>
      )}
    </div>
  );
}
